import { z } from 'zod';

/** Controlli permanenti e indicatori (Fase 6). */

const ident = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
export const CONTROL_FILTER_OPERATORS = ['uguale', 'diverso', 'contiene', 'maggiore', 'minore', 'presente', 'assente'] as const;
/** Filtro su un attributo: maggiore e minore confrontano numeri o date quando il valore lo è. */
const filterSchema = z.object({ attributo: ident, operatore: z.enum(CONTROL_FILTER_OPERATORS), valore: z.string().max(200).default('') });
const filtersSchema = z.array(filterSchema).max(10).default([]);
const keysSchema = z.array(z.object({ a: ident, b: ident, modo: z.enum(['uguale', 'contiene']) })).min(1).max(5);

/**
 * Controlli di gestione (ADR 0018): forme dichiarative che l'archivio esegue in modo deterministico. Sono forme di
 * calcolo, non temi aziendali: cosa controllare lo decide chi progetta il controllo (persona o motore).
 */
const refSchema = z.object({ lato: z.enum(['a', 'b']).default('a'), attributo: ident }).strict();
/** Un termine di una regola: un attributo, un valore fisso, oggi, i giorni tra due date, una data più N giorni. */
export const ControlTermSchema = z.union([
  refSchema,
  z.object({ valore: z.string().max(200) }).strict(),
  z.object({ oggi: z.literal(true) }).strict(),
  z.object({ giorni_tra: z.object({ da: refSchema, a: refSchema }).strict() }).strict(),
  z.object({ data: refSchema, piu_giorni: z.union([z.number().int().min(-3660).max(3660), refSchema]), fine_mese: z.boolean().default(false) }).strict(),
]);
export type ControlTerm = z.infer<typeof ControlTermSchema>;

export const CONTROL_RULE_OPERATORS = ['uguale', 'diverso', 'minore', 'minore_uguale', 'maggiore', 'maggiore_uguale', 'presente'] as const;
/** Regola: sinistra operatore destra, confrontati come numeri, date o testo, con tolleranza (per le date in giorni). */
export const ControlRuleSchema = z.object({
  descrizione: z.string().trim().min(3).max(200),
  sinistra: ControlTermSchema,
  operatore: z.enum(CONTROL_RULE_OPERATORS),
  destra: ControlTermSchema.nullable().default(null),
  confronto: z.enum(['numero', 'data', 'testo']).default('testo'),
  tolleranza: z.number().min(0).max(1e12).default(0),
  tolleranza_percentuale: z.number().min(0).max(100).default(0),
});
export type ControlRule = z.infer<typeof ControlRuleSchema>;

const PERIODS = ['mese', 'trimestre', 'anno'] as const;

export const ControlFormSchema = z.discriminatedUnion('forma', [
  /** Ogni A (filtrato) ha un B con le stesse chiavi; se ci sono regole, almeno un B abbinato le rispetta tutte. */
  z.object({
    forma: z.literal('corrispondenza'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    regole: z.array(ControlRuleSchema).max(5).default([]),
    /** Un A senza nessun B: eccezione o fuori da questo controllo (quando conta solo se i valori tornano). */
    mancante: z.enum(['eccezione', 'ignora']).default('eccezione'),
  }),
  /** Regole su un solo insieme (valori mancanti, fuori intervallo, scadenze rispetto a oggi) e doppioni per chiave. */
  z.object({
    forma: z.literal('regola'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    regole: z.array(ControlRuleSchema).max(5).default([]),
    doppioni: z.array(ident).max(3).default([]),
  }),
  /** Per ogni A attivo in un periodo chiuso c'è almeno un B con data in quel periodo. */
  z.object({
    forma: z.literal('periodicita'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    data_b: ident,
    inizio_a: ident.nullable().default(null),
    fine_a: ident.nullable().default(null),
    periodo: z.enum(PERIODS),
    periodi: z.number().int().min(1).max(24).default(3),
  }),
  /** La somma (o il conteggio) dei B di ogni A rispetta un limite. */
  z.object({
    forma: z.literal('totale'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    tipo_b: ident,
    filtri_b: filtersSchema,
    chiavi: keysSchema,
    metrica: z.enum(['somma', 'conteggio']),
    attributo_b: ident.nullable().default(null),
    operatore: z.enum(['minore_uguale', 'maggiore_uguale', 'uguale']),
    limite: ControlTermSchema,
    tolleranza: z.number().min(0).max(1e12).default(0),
    tolleranza_percentuale: z.number().min(0).max(100).default(0),
  }),
  /** L'ultimo periodo chiuso di un indicatore si scosta dalla media dei precedenti. */
  z.object({
    forma: z.literal('andamento'),
    tipo_a: ident,
    filtri_a: filtersSchema,
    metrica: z.enum(['conteggio', 'somma', 'media']),
    attributo: ident.nullable().default(null),
    attributo_data: ident,
    grana: z.enum(PERIODS).default('mese'),
    periodi: z.number().int().min(2).max(24).default(6),
    scarto_percentuale: z.number().min(5).max(1000),
    direzione: z.enum(['entrambe', 'aumento', 'calo']).default('entrambe'),
  }),
]);
export type ControlForm = z.infer<typeof ControlFormSchema>;
export type ControlFormInput = z.input<typeof ControlFormSchema>;

/** Attributo che serve a un controllo e sta nei documenti: si aggiunge al tipo del modello e si ricava con citazione. */
export const RequiredAttributeSchema = z.object({
  tipo: ident,
  nome: ident,
  tipo_valore: z.enum(['testo', 'numero', 'data']),
  etichetta: z.string().trim().min(1).max(120),
  descrizione: z.string().trim().max(500).default(''),
});
export type RequiredAttribute = z.infer<typeof RequiredAttributeSchema>;

export const CreateControlSchema = z.object({
  name: z.string().trim().min(3).max(120),
  description: z.string().trim().max(500).optional(),
  /** Cosa verificare (ADR 0018). Senza, vale la forma storica qui sotto: ogni A ha un corrispondente in B. */
  verifica: ControlFormSchema.optional(),
  tipo_a: ident.optional(),
  filtri_a: filtersSchema,
  tipo_b: ident.optional(),
  chiavi: keysSchema.optional(),
  /** Attributi di A da mostrare nel compito di verifica. */
  campi: z.array(ident).min(1).max(10),
  /** Ruolo che verifica le eccezioni ed esiti possibili; l'ultimo esito vale "falso allarme". */
  ruolo: ident,
  esiti: z.array(ident).min(2).max(5).default(['da_sistemare', 'non_e_un_problema']),
  /** Dati che il controllo legge dai documenti (attributi aggiunti ai tipi del modello). */
  dati_richiesti: z.array(RequiredAttributeSchema).max(10).default([]),
});
export type CreateControlInput = z.infer<typeof CreateControlSchema>;

/** Esito di un'esecuzione o di una prova: eccezioni con il motivo e copertura dei dati. */
export interface ControlCheckResult {
  totale: number;
  verificabili: number;
  non_verificabili: number;
  eccezioni: Array<{ rif: string; attributi: Record<string, unknown>; motivo: string }>;
  esempi_non_verificabili: Array<{ rif: string; motivo: string }>;
}

export interface ControlDto {
  id: string;
  name: string;
  description: string | null;
  config: CreateControlInput;
  /** Forma effettiva e sua lettura in italiano. */
  verifica: ControlForm;
  spiegazione: string;
  process_id: string;
  enabled: boolean;
  last_run_at: string | null;
  last_result: { totale: number; abbinate: number; nuove: number; aperte: number; risolte: number; non_verificabili?: number } | null;
  /** Esiti delle verifiche: quante eccezioni erano vere e quante falsi allarmi. */
  outcomes: Record<string, number>;
  open_exceptions: number;
}

export const PreviewControlSchema = z.object({ verifica: ControlFormSchema });

export const CreateKpiSchema = z.object({
  name: z.string().trim().min(2).max(120),
  tipo: ident,
  metrica: z.enum(['conteggio', 'somma', 'media']),
  attributo: ident.nullable().default(null),
  filtri: filtersSchema,
  attributo_data: ident.nullable().default(null),
  grana: z.enum(['giorno', 'mese']).nullable().default(null),
});
export type CreateKpiInput = z.infer<typeof CreateKpiSchema>;

export interface KpiDto {
  id: string;
  name: string;
  config: CreateKpiInput;
  version: number;
  serie: Array<{ periodo: string; valore: number; righe: number }>;
  copertura: { righe_totali: number; righe_escluse: number; fonti: Array<{ fonte: string; righe: number; ultimo_aggiornamento: string }> };
  spiegazione: string;
  errore: string | null;
}
